import { createPortal } from 'react-dom';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

type TipAlign = 'center' | 'end';

type ActionTipButtonProps = {
  tooltip: string;
  onClick: () => void;
  children: ReactNode;
  /** Tooltip alignment — `end` keeps labels inside the viewport in table action columns. */
  tipAlign?: TipAlign;
  disabled?: boolean;
};

export function ActionTipButton({
  tooltip,
  onClick,
  children,
  tipAlign = 'end',
  disabled,
}: ActionTipButtonProps) {
  const [open, setOpen] = useState(false);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;

    const updateTipPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      setTipPos({
        x: tipAlign === 'end' ? rect.right : rect.left + rect.width / 2,
        y: rect.top,
      });
    };

    updateTipPosition();
    window.addEventListener('resize', updateTipPosition);
    window.addEventListener('scroll', updateTipPosition, true);
    return () => {
      window.removeEventListener('resize', updateTipPosition);
      window.removeEventListener('scroll', updateTipPosition, true);
    };
  }, [open, tipAlign]);

  const showTip = open;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="act-b"
        aria-label={tooltip}
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </button>
      {showTip && tipPos
        ? createPortal(
            <div
              className="act-tip-portal"
              role="tooltip"
              style={{
                top: tipPos.y - 5,
                left: tipPos.x,
                transform: tipAlign === 'end' ? 'translate(-100%, -100%)' : 'translate(-50%, -100%)',
              }}
            >
              {tooltip}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
